import { Settings2 } from "lucide-react";
import { Button } from "@/shared/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/shared/ui/DropDownMenu";
import { TanStackColumn } from "./index";
import type { AppTable } from "./features";
import type { RowData } from "@tanstack/react-table";

type TanStackColumnsSelectorProps<T extends RowData> = {
  table: AppTable<T>
  columns: TanStackColumn<T>[]
};

const getColumnId = <T extends RowData>(column: TanStackColumn<T>): string | undefined => {
  const accessorKey = (column as { accessorKey?: string }).accessorKey;
  return column.id || accessorKey;
};

export const TanStackColumnsSelector = <T extends RowData>({
  table,
  columns,
}: TanStackColumnsSelectorProps<T>) => {
  // Get all columns that can be hidden/shown (exclude selection and actions columns)
  const selectableColumns = columns.filter((col) => {
    const colId = getColumnId(col);
    return !col.isSelectionColumn && colId !== "actions" && colId;
  });

  const handleToggleAll = () => {
    const allVisible = selectableColumns.every((col) => {
      const colId = getColumnId(col);
      if (!colId) return true; // Skip columns without ID
      const column = table.getColumn(colId);
      if (!column) return true; // Skip non-existent columns
      return column.getIsVisible() !== false;
    });

    // Update visibility state via table handler (triggers localStorage save)
    const currentVisibility = table.store.state.columnVisibility;
    const newVisibility = { ...currentVisibility };
    selectableColumns.forEach((col) => {
      const colId = getColumnId(col);
      if (colId) {
        newVisibility[colId] = !allVisible;
      }
    });

    table.setColumnVisibility(newVisibility);
  };

  const areAllColumnsVisible = selectableColumns.every((col) => {
    const colId = getColumnId(col);
    if (!colId) return true; // Skip columns without ID
    const column = table.getColumn(colId);
    if (!column) return true; // Skip non-existent columns
    return column.getIsVisible() !== false;
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          aria-label="Sélectionner les colonnes à afficher"
        >
          <Settings2 className="h-4 w-4" />
          Colonnes
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuCheckboxItem
          checked={areAllColumnsVisible}
          onCheckedChange={handleToggleAll}
          onSelect={(event) => event.preventDefault()}
          className="font-medium"
        >
          Tout cocher
        </DropdownMenuCheckboxItem>

        <DropdownMenuSeparator />

        {selectableColumns.map((col) => {
          const colId = getColumnId(col);
          if (!colId) return null; // Skip columns without ID

          const column = table.getColumn(colId);
          if (!column) {
            return null;
          }

          const label = col.label || col.header || colId || "Colonne";

          return (
            <DropdownMenuCheckboxItem
              key={colId}
              checked={column.getIsVisible() !== false}
              onCheckedChange={(checked) => {
                const currentVisibility = table.store.state.columnVisibility;
                table.setColumnVisibility({
                  ...currentVisibility,
                  [colId]: !!checked,
                });
              }}
              onSelect={(event) => event.preventDefault()}
            >
              {typeof label === "string" ? label : "Colonne"}
            </DropdownMenuCheckboxItem>
          );
        })}

        {selectableColumns.length === 0 && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            Aucune colonne configurable
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TanStackColumnsSelector;
